import { supportsAdvancedMerchandising } from '../../../app/runtime/adminCompatibilityModel';

export const QUERY_EXTENSION_CAPABILITIES = {
  selection: 'merchandising_result_selection_v1',
  empty: 'merchandising_empty_response_v1',
  destination: 'merchandising_destination_response_v1',
  forms: 'merchandising_query_match_forms_v1',
  concept: 'merchandising_query_match_concepts_v1',
};

export const selectionFields = schema =>
  Array.isArray(schema?.selection_fields)
    ? schema.selection_fields
    : Object.entries(schema?.selection_fields || {}).map(([key, value]) => ({
        ...value,
        key,
      }));

export const advancedQuerySupport = ({
  schema,
  runtime,
} = {}) => {
  const cloudEnabled = supportsAdvancedMerchandising({ productId: runtime });
  const matchContext = schema?.query_match_context || null;
  const locale = typeof matchContext?.locale === 'string' && matchContext.locale.length <= 64
    && /^[A-Za-z]{2,3}(?:[-_][A-Za-z0-9]{2,8})*$/.test(matchContext.locale)
    ? matchContext.locale : null;
  const matchModes = Array.isArray(matchContext?.modes)
    ? matchContext.modes.filter(mode => ['exact', 'forms', 'concept'].includes(mode)) : [];
  const supported = (name, modes, mode) =>
    cloudEnabled &&
    schema?.capabilities?.[QUERY_EXTENSION_CAPABILITIES[name]] === true &&
    (!modes || modes.includes(mode));
  const matchingSupported = mode => Boolean(locale && matchContext?.supported === true
    && typeof matchContext.profile === 'string' && matchContext.profile
    && supported(mode, matchModes, mode));
  return {
    cloudEnabled,
    selectionSourcePreview: cloudEnabled && schema?.capabilities?.merchandising_selection_source_preview_v1 === true,
    richConditions: cloudEnabled && schema?.capabilities?.merchandising_selection_conditions_v2 === true,
    matchContext,
    locale,
    matchModes,
    matchUnsupportedReason: matchContext?.reason || (!matchContext ? 'MATCH_CONTEXT_UNAVAILABLE' : null),
    search: (schema?.response_modes || []).includes('search'),
    selection: supported(
      'selection',
      schema?.response_modes || [],
      'selection'
    ),
    empty: supported('empty', schema?.response_modes || [], 'empty'),
    destination: supported('destination'),
    forms: matchingSupported('forms'),
    concept: matchingSupported('concept'),
    locales: locale ? [locale] : [],
    fields: selectionFields(schema),
    matchPreview: cloudEnabled && Boolean(locale) && matchModes.includes('exact'),
  };
};

const onlyKeys = (object, keys) =>
  object &&
  typeof object === 'object' &&
  !Array.isArray(object) &&
  Object.keys(object).every(key => keys.includes(key));

const selectionValueEditable = (item, descriptor) => {
  if (
    item.include_descendants !== undefined &&
    (item.field !== 'category_ids' ||
      typeof item.include_descendants !== 'boolean')
  )
    return false;
  const plainText = (value, maximum = 500) => typeof value === 'string'
    && value.length <= maximum && Array.from(value).every(character => {
      const code = character.charCodeAt(0);
      return code >= 32 && (code < 127 || code > 159);
    });
  const numeric = value => value === '' || (typeof value === 'number' && Number.isFinite(value));
  const scalar = value => {
    if (descriptor.value_type === 'number') return numeric(value);
    if (descriptor.value_type === 'boolean') return typeof value === 'boolean';
    if (['identifier', 'identifier_list', 'string', 'text'].includes(descriptor.value_type)) return plainText(value);
    if (['scalar', 'scalar_list', 'scalar_or_list'].includes(descriptor.value_type))
      return plainText(value) || typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value));
    return false;
  };
  if (item.operator === 'exists') return typeof item.value === 'boolean';
  if (['text_contains_any', 'text_not_contains_any'].includes(item.operator))
    return Array.isArray(item.value) && item.value.length <= 10
      && item.value.every(value => plainText(value, 100));
  if (['in', 'not_in', 'contains_any', 'contains_all', 'contains_none'].includes(item.operator))
    return (
      Array.isArray(item.value) &&
      item.value.length <= 100 && item.value.every(value => (
        item.field.startsWith('attributes.') && ['in', 'contains_any'].includes(item.operator)
          ? plainText(value) : scalar(value)
      ))
    );
  if (item.operator === 'between') return Array.isArray(item.value)
    && item.value.length === 2 && item.value.every(numeric);
  if (['gt', 'gte', 'lt', 'lte'].includes(item.operator)) return numeric(item.value);
  if (['eq', 'neq'].includes(item.operator)) return scalar(item.value);
  return false;
};

const selectionConditionsEditable = (group, fields, budget, depth = 1) => {
  if (!onlyKeys(group, ['match', 'items']) || !['all', 'any'].includes(group.match)
    || depth > 3 || !Array.isArray(group.items) || group.items.length > 10) return false;
  return group.items.every(item => {
    if (Array.isArray(item?.items)) return selectionConditionsEditable(item, fields, budget, depth + 1);
    budget.source += 1;
    budget.total += 1;
    return budget.source <= 10 && budget.total <= 50
      && onlyKeys(item, ['field', 'operator', 'value', 'include_descendants'])
      && fields.some(field => field.key === item.field && field.operators?.includes(item.operator)
        && selectionValueEditable(item, field));
  });
};

export const queryExtensionEditable = (model, schema) => {
  if (model.extensionMismatch) return false;
  const policy = model.responsePolicy;
  if (policy != null) {
    if (policy.version !== 'query-response.v1') return false;
    if (policy.mode === 'search') {
      if (!onlyKeys(policy, ['version', 'mode'])) return false;
    } else if (policy.mode === 'empty') {
      if (
        !onlyKeys(policy, ['version', 'mode', 'message', 'link']) ||
        typeof policy.message !== 'string'
      )
        return false;
      if (
        policy.link &&
        (!onlyKeys(policy.link, ['label', 'url']) ||
          typeof policy.link.label !== 'string' ||
          typeof policy.link.url !== 'string')
      )
        return false;
    } else if (policy.mode === 'selection') {
      if (
        !onlyKeys(policy, ['version', 'mode', 'sources']) ||
        !Array.isArray(policy.sources) || policy.sources.length > 5
      )
        return false;
      const fields = selectionFields(schema);
      const budget = { total: 0, source: 0 };
      if (
        !policy.sources.every(
          source => {
            budget.source = 0;
            return (
            onlyKeys(source, ['id', 'query', 'conditions']) &&
            typeof source.id === 'string' &&
            (source.query == null || typeof source.query === 'string') &&
            selectionConditionsEditable(source.conditions, fields, budget)
            );
          }
        )
      )
        return false;
    } else return false;
  }
  const config = model.matchConfig;
  if (
    config != null &&
    (config.version !== 'query-match.v1' ||
      !['exact', 'forms', 'concept'].includes(config.mode) ||
      !onlyKeys(config, [
        'version',
        'mode',
        'locale',
        'aliases',
        'exclusions',
      ]) ||
      !Array.isArray(config.aliases) ||
      !Array.isArray(config.exclusions) ||
      !config.aliases
        .concat(config.exclusions)
        .every(value => typeof value === 'string'))
  )
    return false;
  return true;
};

export const queryExtensionError = ({
  rule,
  matchConfig,
  schema,
  runtime,
  bootstrap,
} = {}) => {
  const model = { responsePolicy: rule?.response_policy, matchConfig };
  if (!queryExtensionEditable(model, schema)) return 'unknown';
  const support = advancedQuerySupport({ schema, runtime, bootstrap });
  if (
    model.responsePolicy &&
    (!support.cloudEnabled || !support[model.responsePolicy.mode] ||
      (model.responsePolicy.link && !support.destination))
  )
    return 'unsupported';
  if (
    matchConfig &&
    (!support.cloudEnabled ||
      (matchConfig.mode !== 'exact' && !support[matchConfig.mode]) ||
      !support.matchModes.includes(matchConfig.mode) ||
      !support.locales.includes(matchConfig.locale))
  )
    return 'unsupported';
  return null;
};
